import {
  Directive,
  input,
  ElementRef,
  HostListener,
  type ComponentRef,
  ViewContainerRef,
  type OnDestroy,
  Injector,
  inject,
  DestroyRef
} from '@angular/core';
import { CustomTooltipComponent } from '../components/custom-tooltip/custom-tooltip.component';
import { Overlay, type OverlayRef, OverlayPositionBuilder, type ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

/**
 * Position type for tooltip placement
 */
export type TooltipPosition = 'above' | 'below' | 'left' | 'right';

/**
 * BeFair Tooltip Directive
 *
 * Provides an accessible, modern tooltip experience with flexible positioning
 * and rich content support.
 *
 * @example
 * <!-- Simple text tooltip -->
 * <button [appBefairTooltip]="'Click to save'">Save</button>
 *
 * <!-- Rich tooltip with custom position -->
 * <button
 *   [appBefairTooltip]="''"
 *   tooltipTitle="Quick Actions"
 *   tooltipDescription="Press Ctrl+S to save or Ctrl+P to print"
 *   tooltipPosition="right">
 *   Help
 * </button>
 */
@Directive({
  selector: '[appBefairTooltip]',
  standalone: true,
  host: {
    '[attr.aria-describedby]': 'tooltipId',
    '[class.has-tooltip]': 'true'
  }
})
export class CustomTooltipDirective implements OnDestroy {
  // Input signals for reactive configuration
  readonly tooltipText = input<string>('', { alias: 'appBefairTooltip' });
  readonly tooltipIcon = input<string | undefined>(undefined);
  readonly tooltipTitle = input<string | undefined>(undefined);
  readonly tooltipDescription = input<string | undefined>(undefined);
  readonly tooltipActionText = input<string | undefined>(undefined);
  readonly tooltipActionCallback = input<(() => void) | undefined>(undefined);
  readonly tooltipPosition = input<TooltipPosition>('above');
  readonly tooltipShowDelay = input<number>(300);
  readonly tooltipDisabled = input<boolean>(false);

  // Injected dependencies
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly overlay = inject(Overlay);
  private readonly overlayPositionBuilder = inject(OverlayPositionBuilder);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  // State management
  private overlayRef?: OverlayRef;
  private tooltipRef?: ComponentRef<CustomTooltipComponent>;
  private showTimeout?: ReturnType<typeof setTimeout>;
  private hideTimeout?: ReturnType<typeof setTimeout>;
  private isTooltipVisible = false;

  // Unique ID for accessibility
  protected readonly tooltipId = `befair-tooltip-${Math.random().toString(36).substring(2, 9)}`;

  constructor() {
    // Register cleanup on component destruction
    this.destroyRef.onDestroy(() => {
      this.cleanup();
    });
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    // Don't show if disabled or already visible
    if (this.tooltipDisabled() || this.isTooltipVisible) {
      return;
    }

    // Clear any pending hide timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = undefined;
    }

    // Show tooltip after delay
    this.showTimeout = setTimeout(() => {
      this.show();
      this.isTooltipVisible = true;
    }, this.tooltipShowDelay());
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    // Clear show timeout if still pending
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = undefined;
    }

    // Hide with slight delay to allow moving to tooltip
    this.hideTimeout = setTimeout(() => {
      this.hide();
      this.isTooltipVisible = false;
    }, 100);
  }

  @HostListener('click')
  onClick(): void {
    // Hide tooltip on click
    this.cleanup();
  }

  private show(): void {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.getPositions());

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      hasBackdrop: false
    });

    const tooltipPortal = new ComponentPortal(
      CustomTooltipComponent,
      this.viewContainerRef,
      this.injector,
      undefined
    );

    this.tooltipRef = this.overlayRef.attach(tooltipPortal);

    // Set tooltip content properties
    this.tooltipRef.instance.text = this.tooltipText();
    this.tooltipRef.instance.icon = this.tooltipIcon();
    this.tooltipRef.instance.title = this.tooltipTitle();
    this.tooltipRef.instance.description = this.tooltipDescription();
    this.tooltipRef.instance.actionText = this.tooltipActionText();
    this.tooltipRef.instance.actionCallback = this.tooltipActionCallback();
    this.overlayRef.overlayElement.setAttribute('id', this.tooltipId);
    this.overlayRef.overlayElement.setAttribute('role', 'tooltip');
  }

  private hide(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
      this.tooltipRef = undefined;
    }
    this.isTooltipVisible = false;
  }

  /**
   * Clean up all resources and timers
   */
  private cleanup(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = undefined;
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = undefined;
    }
    this.hide();
  }

  /**
   * Get position strategies for the overlay
   */
  private getPositions(): ConnectedPosition[] {
    const offset = 8;
    const positions: Record<TooltipPosition, ConnectedPosition> = {
      above: {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
        offsetY: -offset
      },
      below: {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
        offsetY: offset
      },
      left: {
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'center',
        offsetX: -offset
      },
      right: {
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center',
        offsetX: offset
      }
    };

    const currentPosition = this.tooltipPosition();
    const primary = positions[currentPosition];

    // Add fallback positions for better UX
    const fallbacks = Object.entries(positions)
      .filter(([key]) => key !== currentPosition)
      .map(([_, pos]) => pos);

    return [primary, ...fallbacks];
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}
